import Icon from 'components/AppIcon';

interface NotificationBellProgressProps {
  progress?: number;
  badgeCount?: number;
  isLoading?: boolean;
  onClick?: () => void;
  className?: string;
}

const NotificationBellProgress = ({
  progress,
  badgeCount = 0,
  isLoading = false,
  onClick,
  className = '',
}: NotificationBellProgressProps) => {
  const containerSize = 40; // Container size
  const iconSize = 20; // Bell icon size
  const strokeWidth = 2.5;
  const radius = (containerSize - strokeWidth * 2) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = progress !== undefined ? circumference - (progress / 100) * circumference : circumference;

  const hasProgress = progress !== undefined && progress >= 0;
  const showBadge = !hasProgress && !isLoading && badgeCount > 0;

  return (
    <button
      onClick={onClick}
      className={`relative flex items-center justify-center hover:bg-neutral-100 rounded transition-colors ${className}`}
      style={{ width: containerSize, height: containerSize }}
      type="button"
      aria-label="Export notifications"
    >
      {/* Bell Icon - Always centered */}
      <div className="relative flex items-center justify-center">
        <Icon name="Bell" size={iconSize} />

        {/* Badge - Top right of bell */}
        {showBadge && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-semibold rounded-full flex items-center justify-center shadow-sm">
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </div>

      {/* Progress Circle Overlay */}
      {hasProgress && (
        <svg
          width={containerSize}
          height={containerSize}
          className="absolute top-0 left-0"
          style={{ transform: 'rotate(-90deg)' }}
        >
          {/* Background circle */}
          <circle
            cx={containerSize / 2}
            cy={containerSize / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="none"
            className="text-neutral-200"
          />
          {/* Progress circle */}
          <circle
            cx={containerSize / 2}
            cy={containerSize / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="text-primary transition-all duration-500 ease-out"
            strokeLinecap="round"
          />
        </svg>
      )}

      {/* Loading Spinner Overlay */}
      {isLoading && !hasProgress && (
        <svg
          width={containerSize}
          height={containerSize}
          className="absolute top-0 left-0 animate-spin"
          style={{ animationDuration: '1s' }}
        >
          <circle
            cx={containerSize / 2}
            cy={containerSize / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${circumference * 0.25} ${circumference * 0.75}`}
            className="text-primary"
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  );
};

export default NotificationBellProgress;
